import type { RouteLocationNormalized } from 'vue-router';

import { CARER_TAB_PERMISSIONS, CLIENT_TAB_PERMISSIONS, POWER_ROLES, REPORT_PERMISSION_KEYS } from '@/constants/roles';

/**
 * Determines the permission key(s) required for a given route
 * @param route - The route being navigated to
 * @returns A single key, a list of keys of which any one grants access, or undefined if no
 *          permission is needed
 */
export const getPermissionKeyForRoute = (route: RouteLocationNormalized): string | string[] | undefined => {
  // First, check if route meta has explicit permission key
  if (route.meta?.permissionKey) {
    return route.meta.permissionKey as string | string[];
  }

  // Reports is granted per category rather than by one page-level key
  if (route.name === 'agency-reports') {
    return REPORT_PERMISSION_KEYS;
  }

  // Handle client detail tabs
  if (route.name === 'agency-clients-view-id-tab' && route.params.tab) {
    const tab = route.params.tab as string;

    return CLIENT_TAB_PERMISSIONS[tab];
  }

  // Handle carer detail tabs
  if (route.name === 'agency-carers-view-id-tab' && route.params.tab) {
    const tab = route.params.tab as string;

    return CARER_TAB_PERMISSIONS[tab];
  }

  return undefined;
};

/**
 * Checks a permission requirement, where a list of keys means any one of them grants access
 * @param requirement - A single key, a list of keys, or undefined when nothing is required
 * @param userHasPermission - Function to check if user has a specific permission
 * @returns True if the requirement is met
 */
export const hasRequiredPermission = (
  requirement: string | string[] | undefined,
  userHasPermission: (permission: string) => boolean
): boolean => {
  if (!requirement) {
    return true;
  }

  if (Array.isArray(requirement)) {
    return requirement.some(key => userHasPermission(key));
  }

  return userHasPermission(requirement);
};

/**
 * Paths that are only accessible to power roles (super admin and admin)
 */
export const POWER_ROLE_ONLY_PATHS = ['/agency/settings', '/agency/activity-log'] as const;

/**
 * Checks if a route is restricted to power roles only (super admin and admin)
 * @param route - The route being navigated to
 * @returns True if the route requires a power role
 */
export const isPowerRoleOnlyRoute = (route: { path: string }): boolean => {
  return POWER_ROLE_ONLY_PATHS.some(path => route.path === path || route.path.startsWith(`${path}/`));
};

/**
 * Checks if a user role has power access (bypasses permission checks)
 * @param roleSlug - The role slug to check (lowercase)
 * @returns True if the role has power access
 */
export const isPowerRole = (roleSlug: string | undefined): boolean => {
  if (!roleSlug) return false;

  return POWER_ROLES.includes(roleSlug.toLowerCase() as any);
};

/**
 * Gets the first available client tab that the user has permission to access
 * @param userHasPermission - Function to check if user has a specific permission
 * @param isPowerUser - Whether the user has power role (admin/superadmin)
 * @returns The first available tab key, defaults to 'profile'
 */
export const getFirstAvailableClientTab = (
  userHasPermission: (permission: string) => boolean,
  isPowerUser: boolean = false
): string => {
  if (isPowerUser) {
    return 'profile';
  }

  const tabOrder = [
    'profile',
    'care-plans',
    'daily-task',
    'medication',
    'schedule',
    'emar',
    'absence',
    'contacts',
    'carer-ratings',
    'notes',
    'activity-log',
    'share-client-data'
  ];

  for (const tab of tabOrder) {
    const permission = CLIENT_TAB_PERMISSIONS[tab];
    if (!permission || userHasPermission(permission)) {
      return tab;
    }
  }

  return 'profile';
};

/**
 * Gets the first available carer tab that the user has permission to access
 * @param userHasPermission - Function to check if user has a specific permission
 * @param isPowerUser - Whether the user has power role (admin/superadmin)
 * @returns The first available tab key, defaults to 'profile'
 */
export const getFirstAvailableCarerTab = (
  userHasPermission: (permission: string) => boolean,
  isPowerUser: boolean = false
): string => {
  if (isPowerUser) {
    return 'profile';
  }

  const tabOrder = ['profile', 'absence', 'notes', 'activity-log'];

  for (const tab of tabOrder) {
    const permission = CARER_TAB_PERMISSIONS[tab];
    if (!permission || userHasPermission(permission)) {
      return tab;
    }
  }

  return 'profile';
};
